import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardCheck, Search, ChevronRight, Inbox, Banknote, Boxes, ShoppingCart, Wrench, Users,
  MoreHorizontal, CalendarDays, Zap, Plus, FileText, UserPlus, BarChart3, Target, Sprout, X,
} from 'lucide-react';
import { Skeleton, Alert, Input, Select, cn } from '../../ui';
import { usePermissionChecker } from '../../store/auth';
import { request } from '../../api/client';
import { useWorklist } from '../../hooks/useWorklist';
import { useUrlBool, useUrlString } from '../../hooks/useUrlState';
import type { WorkCategory, WorkItem } from '../../hooks/useWorklist';
import { CATEGORY_LABEL, WorkAllClear, WorkListRow } from '../../components/WorkItemList';
import { formatIst } from '../../lib/time';
import { inr } from '../../lib/format';

/* ─────────────────────────────────────────────────────────────────────────────
 * Tabs and tiles both slice the SAME list, so they share one value. `all` is the
 * whole list; `money` is every row carrying an amount (a slice that cuts across
 * subjects); the rest are the subjects themselves.
 * ───────────────────────────────────────────────────────────────────────────── */
type Lane = 'all' | 'money' | `cat:${WorkCategory}`;
const catLane = (c: WorkCategory): Lane => `cat:${c}`;

/**
 * Is this string a lane this page can actually show?
 *
 * The lane comes from the query string, so it can arrive from a stale bookmark, a hand-edited URL,
 * or a chip in the login greeting written before a category existed. An unrecognised lane matches no
 * row, and the page would answer a bad address with "Nothing here matches" — which reads as an
 * empty worklist, the most misleading thing this screen can say. Junk falls back to the whole list.
 */
const CATS: WorkCategory[] = ['money', 'orders', 'stock', 'people', 'service', 'general'];
const isLane = (v: string): v is Lane =>
  v === 'all' || v === 'money' || (v.startsWith('cat:') && (CATS as string[]).includes(v.slice(4)));

function inLane(i: WorkItem, lane: Lane): boolean {
  if (lane === 'all') return true;
  if (lane === 'money') return !!i.amount;
  return i.category === lane.slice(4);
}

/* Tab and tile wording comes from CATEGORY_LABEL, the same map the row chips and the login
 * greeting's breakdown read — a tab named "Payments" filtering rows chipped "money" was one
 * rename away from being two different subjects to a reader who did not know they were the same. */
const CAT_ICON: Record<WorkCategory, ReactNode> = {
  money: <Banknote />, orders: <ShoppingCart />, stock: <Boxes />,
  people: <Users />, service: <Wrench />, general: <MoreHorizontal />,
};
/** The tile's tint — the same six families the row icons and chips use. */
const TILE: Record<string, { chip: string; wash: string }> = {
  all: { chip: 'bg-danger-500/12 text-danger-600 dark:text-danger-500', wash: 'border-danger-500/25 from-danger-500/[0.07]' },
  money: { chip: 'bg-harvest-400/20 text-harvest-700 dark:text-harvest-300', wash: 'border-harvest-400/35 from-harvest-400/[0.10]' },
  orders: { chip: 'bg-brand-500/15 text-brand-700 dark:text-brand-300', wash: 'border-brand-500/30 from-brand-500/[0.08]' },
  stock: { chip: 'bg-sky-500/15 text-sky-600 dark:text-sky-400', wash: 'border-sky-500/30 from-sky-500/[0.08]' },
  people: { chip: 'bg-plum-400/15 text-plum-600 dark:text-plum-400', wash: 'border-plum-400/35 from-plum-400/[0.09]' },
  service: { chip: 'bg-danger-500/12 text-danger-600 dark:text-danger-500', wash: 'border-danger-500/25 from-danger-500/[0.07]' },
  general: { chip: 'bg-surface-3 text-muted', wash: 'border-line from-line/40' },
};

type SortKey = 'oldest' | 'count' | 'money';
const SORT_OPTIONS = [
  { value: 'oldest', label: 'Waiting longest' },
  { value: 'count', label: 'Most items' },
  { value: 'money', label: 'Most money' },
];
const SORTERS: Record<SortKey, (a: WorkItem, b: WorkItem) => number> = {
  // `?? -1` puts rows with no age (a standing backlog, not a queue) after the ones that have been
  // waiting, rather than treating "no age" as zero days and floating them to the top.
  oldest: (a, b) => (b.oldestDays ?? -1) - (a.oldestDays ?? -1),
  count: (a, b) => b.count - a.count,
  money: (a, b) => (b.amount ?? 0) - (a.amount ?? 0),
};

const QUICK_TONE = {
  brand: 'bg-brand-500/10 text-brand-700 hover:bg-brand-500/18 dark:text-brand-300',
  sky: 'bg-sky-500/10 text-sky-600 hover:bg-sky-500/18 dark:text-sky-400',
  plum: 'bg-plum-400/12 text-plum-600 hover:bg-plum-400/20 dark:text-plum-400',
  gold: 'bg-harvest-400/15 text-harvest-700 hover:bg-harvest-400/25 dark:text-harvest-300',
};

/** Quick actions, each gated on the permission its own route is gated on in App.tsx. */
const QUICK: { label: string; to: string; icon: ReactNode; perms: string[]; tone: keyof typeof QUICK_TONE }[] = [
  { label: 'Create order', to: '/orders/new', icon: <Plus />, perms: ['ORDER.CREATE'], tone: 'brand' },
  { label: 'Invoices', to: '/billing/invoices', icon: <FileText />, perms: ['INVOICE.VIEW'], tone: 'sky' },
  { label: 'Add customer', to: '/people', icon: <UserPlus />, perms: ['RETAILER.VIEW', 'FARMER.VIEW', 'USER.VIEW'], tone: 'plum' },
  { label: 'View reports', to: '/reports', icon: <BarChart3 />, perms: ['REPORT.RUN'], tone: 'gold' },
];

/** An upcoming event, as the schedule panel needs it. */
interface EventRow { id: number | string; title: string; startAt: string; type?: string; venue?: string | null; status?: string }

/**
 * "My Work" — the whole of what is waiting on this person, and where the login greeting links to.
 *
 * Deliberately ungated: every row inside is produced by services that already permission-gate and
 * row-scope their own numbers, so a user with nothing pending simply sees the all-clear. Gating the
 * ROUTE would only mean the people with the least to do (and the least app experience) get a 403.
 *
 * The page COUNTS NOTHING ITSELF — every figure is a fold over the rows the server sent, so it can
 * never disagree with the nav badge, the approvals hub or the login greeting. The one extra call is
 * the schedule panel's, and it is the real events list, permission-gated like the page it links to.
 */
export function MyWorkPage() {
  const { data, isLoading, isError } = useWorklist();
  const navigate = useNavigate();
  const hasPerm = usePermissionChecker();
  /*
   * Lane, search and sort live in the URL, not in component state. Open a row from a filtered list
   * and press Back and the filters come back with you; and it is what lets the login greeting's
   * chips link straight to "just the stock ones" instead of only being able to open the whole page.
   */
  const [laneParam, setLane] = useUrlString<Lane>('lane', 'all');
  const lane: Lane = isLane(laneParam) ? laneParam : 'all';
  const [query, setQuery] = useUrlString('q', '');
  const [sort, setSort] = useUrlString<SortKey>('sort', 'oldest');
  /*
   * "Only the ones assigned to me" — a NARROWING OF THE WHOLE PAGE rather than one more lane.
   *
   * It used to be `lane: 'act'`, which meant it could not be combined with a subject: press "Pending
   * items" and the subject went, press a subject and the "pending" went. That is also what broke the
   * promise the login greeting's chips make. The greeting counts only work assigned to YOU, so its
   * chip says "Others 1" — and following it landed on a tab reading "Others (3)", because the page
   * was counting the rows sitting with somebody else too. Two screens, one subject, two figures.
   *
   * As a flag it composes: every tile, tab, total and count below folds over `base`, so `?mine=1`
   * recounts the entire page to the reader's own work and the greeting's figure survives the click.
   */
  const [mine, setMine] = useUrlBool('mine', false);

  const items = data?.items ?? [];
  // The navbar badge's figure. Deliberately over `items`, never `base`: it is what the menu item
  // that opened this page says, and it must not change when the reader narrows the view.
  const actCount = useMemo(() => items.filter((i) => i.group === 'ACT').length, [items]);

  // ── Lane + search + sort ──. Declared up here because the tile strip needs it to decide which
  // tile reads as selected: a search narrows the list past any lane, so no tile is "the" filter.
  const q = query.trim().toLowerCase();

  /** Everything the page counts, tabs and lists — one set, so no two figures can disagree. */
  const base = useMemo(() => (mine ? items.filter((i) => i.group === 'ACT') : items), [items, mine]);

  // ── Headline figures, all folds over the same rows ──
  const moneyTotal = useMemo(() => base.reduce((s, i) => s + (i.amount ?? 0), 0), [base]);
  const byCat = useMemo(() => {
    const m = new Map<WorkCategory, number>();
    for (const i of base) m.set(i.category, (m.get(i.category) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [base]);

  /*
   * The strip: the whole list, the money cutting across it, then the biggest subjects — capped so it
   * stays one row of tiles. Each tile IS the filter it describes, so a figure the reader can see is
   * a figure they can open (kpi-tiles-must-drill), and the tab row below shows the same selection.
   */
  const tiles = useMemo(() => {
    type Tile = { key: string; label: string; value: ReactNode; tone: string; icon: ReactNode; on: boolean; press: () => void };
    const out: Tile[] = [
      // `actCount`, NOT `items.length`: this figure is the one the navbar badge shows, and a page
      // whose headline tile disagrees with the menu item that opened it is worse than no tile. The
      // extra WAIT rows are still in the list — the "All" tab counts those, and says so by its name.
      { key: 'mine', label: 'Pending items', value: actCount, tone: 'all', icon: <Inbox />, on: mine, press: () => setMine(!mine) },
    ];
    // "₹0" is never rendered anywhere in this app — a rupee sign with nothing behind it reads as a
    // broken figure rather than an empty queue, so the tile is simply absent.
    if (moneyTotal > 0) {
      out.push({
        key: 'money', label: 'Money waiting', value: inr(moneyTotal), tone: 'money', icon: <Banknote />,
        on: lane === 'money' && !q, press: () => setLane(lane === 'money' ? 'all' : 'money'),
      });
    }
    for (const [cat, n] of byCat) {
      if (out.length >= 6) break;
      const l = catLane(cat);
      out.push({
        key: l, label: CATEGORY_LABEL[cat], value: n, tone: cat, icon: CAT_ICON[cat],
        on: lane === l && !q, press: () => setLane(lane === l ? 'all' : l),
      });
    }
    return out;
  }, [actCount, byCat, moneyTotal, lane, q, mine, setLane, setMine]);

  const rows = useMemo(() => {
    const kept = base.filter((i) => inLane(i, lane) && (!q || `${i.title} ${i.hint}`.toLowerCase().includes(q)));
    // Copy before sorting: `base` is derived from react-query's cached array and must not be
    // mutated in place.
    return [...kept].sort(SORTERS[sort]);
  }, [base, lane, q, sort]);

  const filtered = lane !== 'all' || !!q || mine;
  // One history entry for the lot: the URL-state setters coalesce writes made in the same handler.
  const clear = () => { setLane('all'); setQuery(''); setMine(false); };

  const quick = QUICK.filter((a) => a.perms.some((p) => hasPerm(p)));
  const canSeeEvents = hasPerm('EVENT.VIEW');
  /*
   * Today's planned visits belong in "My schedule" too (V-13). The panel listed events only, so a rep whose work
   * list said "Visit 2 shops today" read "Nothing scheduled" right beside it. Only field staff carry a plan; for
   * anyone else the call returns no stops and the panel is unchanged.
   */
  const canSeePlan = hasPerm('COVERAGE.VIEW') && (hasPerm('ATTENDANCE.SELF') || hasPerm('VISIT.CREATE'));
  const { data: planToday } = useQuery({
    queryKey: ['my-plan-today', 'mywork'],
    queryFn: () => request<any[]>({ url: '/coverage/schedule/my-day' }),
    enabled: canSeePlan,
    staleTime: 60_000,
  });
  const stops = useMemo(() => (planToday ?? []).filter((s) => s.status !== 'CANCELLED'), [planToday]);
  const stopsDone = stops.filter((s) => s.status === 'COMPLETED').length;

  const { data: events } = useQuery({
    queryKey: ['mywork-events'],
    queryFn: () => request<EventRow[]>({ url: '/events' }),
    enabled: canSeeEvents,
    staleTime: 5 * 60_000,
  });
  // Real diary entries only: what has not happened yet, soonest first. No invented agenda.
  const schedule = useMemo(() => {
    const now = Date.now();
    return (events ?? [])
      .filter((e) => e.startAt && new Date(e.startAt).getTime() >= now && e.status !== 'CANCELLED')
      .sort((a, b) => +new Date(a.startAt) - +new Date(b.startAt))
      .slice(0, 4);
  }, [events]);

  return (
    <div className="mx-auto w-full max-w-[1560px]">
      {/* ── Title + banner ─────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-brand-500/12 text-brand-600 [&>svg]:size-7 dark:text-brand-400">
            <ClipboardCheck />
          </span>
          <div className="min-w-0">
            {/* "My Work", not "Super's work". The endpoint returns a FIRST name, and a possessive
                built from it reads as someone else's screen — the reader is looking at their own. */}
            <h1 className="type-hero text-content">My Work</h1>
            {/* No count here: it is the "Pending items" tile's figure, printed a second time 60px
                below itself. This says what the page IS instead. */}
            <p className="mt-0.5 type-body text-muted">Everything that needs your attention</p>
          </div>
        </div>

        <div className="relative flex-1 overflow-hidden rounded-2xl border border-line bg-linear-to-r from-brand-50 via-brand-50/40 to-surface px-6 py-5 shadow-e1 lg:max-w-[46%] dark:from-brand-900/25 dark:via-surface dark:to-surface">
          <div className="pointer-events-none absolute inset-0" aria-hidden>
            <Sprout className="absolute -right-4 -bottom-6 size-40 rotate-12 text-brand-600/[0.07]" />
            <div className="absolute -right-10 -top-14 size-40 rounded-full bg-brand-500/10 blur-3xl" />
          </div>
          <div className="relative max-w-[30rem]">
            <div className="type-body-strong text-content">Stay on top of your work</div>
            <p className="mt-1 type-label text-muted">
              Approve, review and act on everything waiting across the business — the list below is the whole of it.
            </p>
          </div>
        </div>
      </div>

      {isError && (
        <Alert type="error" title="Could not load your work"
          description="Please check your internet and try again in a moment." />
      )}

      {isLoading && (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-[86px] rounded-2xl" />)}
          </div>
          <Skeleton className="h-[460px] rounded-card" />
        </>
      )}

      {!isLoading && !isError && (items.length === 0 ? <WorkAllClear /> : (
        <>
          {/* ── The strip of slices ──────────────────────────────────────────── */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {tiles.map((t) => {
              const on = t.on;
              const tone = TILE[t.tone] ?? TILE.general;
              return (
                <button
                  key={t.key}
                  type="button"
                  aria-pressed={on}
                  onClick={t.press}
                  className={cn(
                    'group flex items-center gap-3 rounded-2xl border bg-surface p-3.5 text-left shadow-e1 transition-shadow duration-150',
                    'bg-linear-to-br to-70% to-transparent hover:shadow-e2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
                    tone.wash,
                    // Selected is carried by depth and a heavier edge rather than a second colour:
                    // the tile already means its own subject, and a green ring on a gold tile would
                    // put two unrelated hues on one card.
                    on && 'shadow-e2 ring-2 ring-inset ring-brand-500/35',
                  )}
                >
                  <span className={cn('grid size-10 shrink-0 place-items-center rounded-xl sm:size-11 [&>svg]:size-5', tone.chip)} aria-hidden>
                    {t.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate type-metric-sm text-content">{t.value}</span>
                    <span className="block truncate type-label text-muted">{t.label}</span>
                  </span>
                  {/* The chevron is the first thing to go on a phone: two tiles to a 412px row leave
                      ~72px for the figure, and "₹85,249" needs more than that. The whole tile is the
                      control either way, so the affordance costs nothing where the figure would clip. */}
                  <ChevronRight className="hidden size-4 shrink-0 text-subtle transition-transform group-hover:translate-x-0.5 sm:block" aria-hidden />
                </button>
              );
            })}
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_352px] xl:items-start">
            {/* ── The list, in one card with its own controls ────────────────── */}
            <div className="min-w-0 overflow-hidden rounded-card border border-line bg-surface shadow-e1">
              <div className="flex flex-col gap-3 border-b border-line px-4 py-3 xl:flex-row xl:items-center xl:justify-between">
                {/* Tabs. The counts are the same folds the tiles use, so the two can never disagree. */}
                <div className="-mb-3 flex min-w-0 gap-1 overflow-x-auto pb-1">
                  <TabButton on={lane === 'all'} onClick={() => setLane('all')} label="All" n={base.length} />
                  {byCat.map(([cat, n]) => (
                    <TabButton key={cat} on={lane === catLane(cat)} onClick={() => setLane(catLane(cat))} label={CATEGORY_LABEL[cat]} n={n} />
                  ))}
                </div>
                <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search your pending work…"
                    prefix={<Search className="size-4 text-subtle" />}
                    className="w-full sm:w-[230px]"
                  />
                  <Select value={sort} onChange={(v: any) => setSort(v)} options={SORT_OPTIONS} className="w-full sm:w-[168px]" />
                </div>
              </div>

              {filtered && (
                <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-2 px-4 py-2 type-label text-muted">
                  Showing <b className="type-label-strong text-content">{rows.length}</b> of {items.length}
                  {mine && <span className="rounded-pill bg-brand-500/10 px-2 py-0.5 type-label-strong text-brand-700 dark:text-brand-300">assigned to you</span>}
                  <button type="button" onClick={clear}
                    className="inline-flex items-center gap-1 rounded-pill px-2 py-0.5 type-label text-brand-600 underline-offset-2 hover:bg-surface-3 hover:underline">
                    <X className="size-3" /> Show everything
                  </button>
                </div>
              )}

              {rows.length ? (
                rows.map((i, n) => <WorkListRow key={i.key} item={i} first={n === 0} />)
              ) : (
                <div className="px-6 py-14 text-center">
                  <p className="type-body-strong text-content">Nothing here matches</p>
                  <p className="mt-1 type-label text-subtle">Try another tab, or clear the search.</p>
                </div>
              )}
            </div>

            {/* ── Rail ───────────────────────────────────────────────────────── */}
            <div className="space-y-4 xl:sticky xl:top-24">
              {(canSeeEvents || stops.length > 0) && (
                <RailCard
                  icon={<CalendarDays className="size-4 text-brand-600 dark:text-brand-400" />}
                  title="My schedule"
                  action={canSeeEvents ? <button type="button" onClick={() => navigate('/events')}
                    className="type-label-strong text-brand-600 hover:text-brand-700">View calendar</button> : undefined}
                >
                  <div className="type-label text-subtle">{formatIst(new Date(), 'ddd, DD MMM YYYY')}</div>
                  {stops.length > 0 && (
                    <div className="mt-3" data-slot="plan-today">
                      <button type="button" onClick={() => navigate('/ffa/my-day')}
                        className="flex w-full items-center justify-between gap-2 text-left type-label-strong text-content hover:text-brand-700">
                        <span>Visits today · {stopsDone} of {stops.length} done</span>
                        <ChevronRight className="size-4 shrink-0 text-subtle" />
                      </button>
                      <ul className="mt-2 space-y-2">
                        {stops.slice(0, 5).map((s) => {
                          const done = s.status === 'COMPLETED';
                          return (
                            <li key={s.id} className="flex items-start gap-2.5">
                              <span className={cn('mt-1.5 size-2 shrink-0 rounded-pill',
                                done ? 'bg-success-500' : s.status === 'STARTED' ? 'bg-sky-500' : 'bg-warning-500')} aria-hidden />
                              <span className="min-w-0 flex-1">
                                <span className={cn('block wrap-break-word type-label-strong', done ? 'text-subtle line-through' : 'text-content')}>{s.customerName}</span>
                                <span className="block type-label text-subtle">
                                  {done ? 'Visited' : s.status === 'STARTED' ? 'In progress' : 'Planned'}{s.place ? ` · ${s.place}` : ''}
                                </span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      {stops.length > 5 && (
                        <p className="mt-2 type-label text-subtle">and {stops.length - 5} more on My Field Day</p>
                      )}
                    </div>
                  )}
                  {canSeeEvents && (<>
                  {/*
                    * REAL diary entries only. The reference design for this panel showed a day of
                    * invented meetings ("Team sync", "End of day review"); this reads the events the
                    * user is actually on, and says so plainly when there are none. A fabricated
                    * agenda on an operations screen is worse than an empty one.
                    */}
                  {schedule.length ? (
                    <ul className="mt-3 space-y-3">
                      {schedule.map((e) => (
                        <li key={e.id}>
                          <button type="button" onClick={() => navigate(`/events/${e.id}`)}
                            className="flex w-full items-start gap-2.5 text-left transition-opacity hover:opacity-80">
                            <span className="w-[62px] shrink-0 type-label-strong tabular-nums text-content">
                              {formatIst(e.startAt, 'DD MMM')}
                            </span>
                            <span className="mt-1.5 size-2 shrink-0 rounded-pill bg-brand-500" aria-hidden />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate type-label-strong text-content">{e.title}</span>
                              <span className="block truncate type-label text-subtle">
                                {formatIst(e.startAt, 'hh:mm A')}{e.venue ? ` · ${e.venue}` : ''}
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 type-label text-subtle">No events coming up. Events you are part of appear here.</p>
                  )}
                  </>)}
                </RailCard>
              )}

              {quick.length > 0 && (
                <RailCard icon={<Zap className="size-4 text-brand-600 dark:text-brand-400" />} title="Quick actions">
                  {/* The things people come here to START, rather than to clear. Each is gated on the
                      same permission as the route it opens. */}
                  <div className="grid grid-cols-2 gap-2">
                    {quick.map((a) => (
                      <button key={a.to} type="button" onClick={() => navigate(a.to)}
                        className={cn(
                          'flex items-center gap-2 rounded-control px-3 py-2.5 text-left type-label-strong transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 [&>svg]:size-4',
                          QUICK_TONE[a.tone],
                        )}>
                        {a.icon}
                        <span className="min-w-0 truncate">{a.label}</span>
                      </button>
                    ))}
                  </div>
                </RailCard>
              )}

              {/* Encouragement, not data — the one card here that states nothing about the business,
                  so it can never be wrong. It closes the rail rather than leaving it ragged. */}
              <div className="relative overflow-hidden rounded-card border border-brand-500/25 bg-linear-to-br from-brand-500/12 to-surface p-4 shadow-e1">
                <Sprout className="pointer-events-none absolute -right-3 -bottom-4 size-24 rotate-12 text-brand-600/10" aria-hidden />
                <div className="relative flex items-start gap-2.5">
                  <Target className="mt-0.5 size-5 shrink-0 text-brand-600 dark:text-brand-400" aria-hidden />
                  <div className="min-w-0">
                    <div className="type-body-strong text-content">
                      {actCount ? 'Small steps make big progress' : 'You are all caught up'}
                    </div>
                    <p className="mt-0.5 type-label text-muted">
                      {actCount ? 'Clear the oldest one first — it has waited the longest.' : 'Nothing is waiting on you. Well done.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ))}
    </div>
  );
}

/** One tab: the subject and how many rows it holds, underlined when it is the one being shown. */
function TabButton({ on, onClick, label, n }: { on: boolean; onClick: () => void; label: string; n: number }) {
  return (
    <button
      type="button" onClick={onClick} aria-pressed={on}
      className={cn(
        'shrink-0 whitespace-nowrap border-b-2 px-3 pb-2.5 pt-1 type-label transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        on ? 'border-brand-600 type-label-strong text-brand-700 dark:text-brand-300'
          : 'border-transparent text-muted hover:text-content',
      )}
    >
      {label} ({n})
    </button>
  );
}

/** A rail panel: tinted header rule, optional action on the right. */
function RailCard({ icon, title, action, children }: { icon: ReactNode; title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface shadow-e1">
      <div className="flex items-center justify-between gap-2 border-b border-line bg-linear-to-b from-brand-500/[0.06] to-transparent px-4 py-3">
        <span className="flex min-w-0 items-center gap-2">
          {icon}
          <span className="truncate type-body-strong text-content">{title}</span>
        </span>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}
